import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { push } from 'connected-react-router';
import Collapse from '@material-ui/core/Collapse';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  DATA_TYPES,
  ENTITY_DATA_TYPE,
  OVERLAP_DATA_TYPE
} from '@components/config/constants';
import MenuIcon from '@components/portal/menu/icon';
import OverflowTooltip from '@shared/overflow-tooltip';
import { getBasePath, getDataType } from '@utils/dashboard-utils';
import { verifyPermissions } from '@utils/permission-utils';
import './menu-section.scss';

// Convert legacy menu item type to dataType/subType (same logic as Portal's convertLegacyItems)
const convertType = (type, subtype, parentDataType = null) => {
  let dataType = type === 'overlaps' ? OVERLAP_DATA_TYPE : type;
  let subType = subtype;
  const typeInfo = DATA_TYPES[dataType];

  // If type is not in DATA_TYPES, it's an entity subtype (e.g., 'project', 'event')
  if (!typeInfo) {
    subType = dataType;
    // If parent is overlap, keep overlap as dataType; otherwise use entity
    dataType = parentDataType === OVERLAP_DATA_TYPE ? OVERLAP_DATA_TYPE : ENTITY_DATA_TYPE;
  }

  return { dataType, subType, typeInfo: DATA_TYPES[dataType] };
};

const MenuSectionItem = ({
  item: {
    children,
    icon, label, permissions, subtype, type
  },
  parentDataType = null
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { dataType: currentDataType, subType: currentSubType } = params || {};
  const [open, setOpen] = useState(false);

  // Convert the menu item type to proper dataType/subType
  const { dataType, subType, typeInfo } = useMemo(
    () => convertType(type, subtype, parentDataType),
    [type, subtype, parentDataType]
  );

  // Build URL using the portal from DATA_TYPES and proper dataType/subType
  const link = useMemo(() => {
    const portal = typeInfo?.portal || getBasePath();
    if (typeInfo?.subType && subType) {
      return `/${portal}/${dataType}/${subType}`;
    }
    return `/${portal}/${dataType}`;
  }, [dataType, subType, typeInfo]);

  const onClick = useCallback(
    () => {
      if (children) {
        setOpen(!open);
      } else {
        dispatch(push(link));
      }
    },
    [children, dispatch, link, open]
  );

  // Auto-expand when current route matches this item's dataType
  const topLevelDataType = getDataType({ dataType: currentDataType }, location);
  useEffect(() => {
    if (children && topLevelDataType === dataType) {
      setOpen(true);
    }
  }, [children, dataType, topLevelDataType]);

  if (!verifyPermissions(permissions)) {
    return null;
  }

  // Determine active state based on item type
  const getActiveState = () => {
    if (children) {
      return topLevelDataType === dataType;
    }
    if (subType) {
      return currentSubType === subType && topLevelDataType === dataType;
    }
    return dataType === topLevelDataType;
  };
  const active = getActiveState();

  // Render item with children (collapsible submenu like overlaps)
  if (children) {
    return (
      <Fragment>
        <div
          onClick={onClick}
          role="presentation"
          styleName={active ? 'menu-item has-children active' : 'menu-item has-children'}
        >
          <span styleName="dropdown-arrow">
            {open ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
          </span>
          {icon && (
            <span styleName="menu-icon">
              <MenuIcon icon={icon} type={subType} active={active} />
            </span>
          )}
          <OverflowTooltip>{label}</OverflowTooltip>
        </div>
        <Collapse in={open}>
          <div styleName="submenu">
            {children.map(child => (
              <MenuSectionItem
                key={child.type}
                item={child}
                parentDataType={dataType}
              />
            ))}
          </div>
        </Collapse>
      </Fragment>
    );
  }

  return (
    <div
      onClick={onClick}
      role="presentation"
      styleName={active ? 'menu-item active' : 'menu-item'}
    >
      {icon && (
        <span styleName="menu-icon">
          <MenuIcon icon={icon} type={subType} active={active} />
        </span>
      )}
      <OverflowTooltip>{label}</OverflowTooltip>
    </div>
  );
};

MenuSectionItem.propTypes = {
  item: PropTypes.object,
  parentDataType: PropTypes.string
};

export default MenuSectionItem;
